import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGallery } from '../context/GalleryContext';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaHeart, FaShare, FaDownload, FaArrowLeft, FaMapMarkerAlt, FaCalendar, FaCamera } from 'react-icons/fa';
import './PhotoDetail.css';
import { Photo } from '../types';

const PhotoDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    // @ts-ignore
    const { photos, favorites, toggleFavorite, fetchArtistPhotos } = useGallery();
    const [photo, setPhoto] = useState<Photo | null>(null);
    const [relatedPhotos, setRelatedPhotos] = useState<Photo[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Try to find the photo in the existing context first
        const foundPhoto = photos.find((p: Photo) => p.id === id) || favorites.find((p: Photo) => p.id === id);

        if (foundPhoto) {
            setPhoto(foundPhoto);
            setLoading(false);

            // Fetch related photos from the same artist
            fetchArtistPhotos(foundPhoto.user.id)
                .then((data: Photo[]) => {
                    setRelatedPhotos(data.filter(p => p.id !== id).slice(0, 4));
                })
                .catch((err: any) => console.error("Could not fetch related photos", err));
        } else {
            // Ideally we would fetch the single photo here if not found in context
            // But for now we rely on context
            setLoading(false);
        }
    }, [id, photos, favorites, fetchArtistPhotos]);

    if (loading) return <div className="detail-loading"><div className="spinner"></div></div>;
    if (!photo) return <div className="detail-error"><h2>Photo not found</h2><Link to="/" className="back-btn">Go Home</Link></div>;

    const isFavorite = favorites.some((fav: Photo) => fav.id === photo.id);

    const handleDownload = () => {
        window.open(photo.urls.full, '_blank');
    };

    const handleToggleFavorite = () => {
        if (!currentUser) {
            if (window.confirm("Please login to save favorites!")) {
                navigate('/login');
            }
            return;
        }
        if (photo) {
            toggleFavorite(photo);
        }
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: photo.alt_description || 'Artwork',
                url: photo.urls.regular,
            });
        } else {
            navigator.clipboard.writeText(photo.urls.regular);
            alert('Link copied to clipboard!');
        }
    };

    return (
        <motion.div
            className="photo-detail-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Blurred Backdrop */}
            <div
                className="detail-backdrop"
                style={{ backgroundImage: `url(${photo.urls.regular})` }}
            ></div>

            <div className="detail-content">
                <div className="detail-nav">
                    <Link to="/" className="back-nav-btn">
                        <FaArrowLeft /> Back to Gallery
                    </Link>
                </div>

                <div className="detail-main">
                    {/* Main Image Stage */}
                    <motion.div
                        className="detail-image-stage"
                        layoutId={`photo-${photo.id}`}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5, type: 'spring' }}
                    >
                        <img src={photo.urls.regular} alt={photo.alt_description || 'Photo'} />

                        <div className="detail-floating-actions">
                            <button
                                className={`fab-btn ${isFavorite ? 'active' : ''}`}
                                onClick={handleToggleFavorite}
                                title="Toggle Favorite"
                            >
                                <FaHeart />
                            </button>
                            <button className="fab-btn" onClick={handleShare} title="Share">
                                <FaShare />
                            </button>
                            <button className="fab-btn" onClick={handleDownload} title="Download HD">
                                <FaDownload />
                            </button>
                        </div>
                    </motion.div>

                    {/* Info Panel */}
                    <motion.div
                        className="detail-info-panel"
                        initial={{ x: 50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <div className="info-header">
                            <div className="artist-profile">
                                <img src={photo.user.profile_image.medium} alt={photo.user.name} />
                                <div>
                                    <h3>{photo.user.name}</h3>
                                    <span className="username">@{photo.user.username}</span>
                                </div>
                            </div>
                            {photo.user.portfolio_url && (
                                <a href={photo.user.portfolio_url} target="_blank" rel="noreferrer" className="follow-btn">
                                    View Profile
                                </a>
                            )}
                        </div>

                        <div className="info-body">
                            {photo.description && <p className="description">"{photo.description}"</p>}

                            <div className="meta-grid">
                                {photo.location && (
                                    <div className="meta-item">
                                        <FaMapMarkerAlt />
                                        <span>{photo.location.title || photo.location.name || 'Unknown Location'}</span>
                                    </div>
                                )}
                                <div className="meta-item">
                                    <FaCalendar />
                                    <span>{new Date(photo.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="meta-item">
                                    <FaHeart />
                                    <span>{photo.likes} Likes</span>
                                </div>
                                {photo.exif && photo.exif.name && (
                                    <div className="meta-item">
                                        <FaCamera />
                                        <span>{photo.exif.name}</span>
                                    </div>
                                )}
                            </div>

                            <div className="tags-list">
                                {photo.tags && photo.tags.map((tag: any, i: number) => (
                                    <span key={i} className="tag">#{tag.title}</span>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {relatedPhotos.length > 0 && (
                    <div className="related-section">
                        <h3>More by {photo.user.name}</h3>
                        <div className="related-grid">
                            {relatedPhotos.map(p => (
                                <Link to={`/photo/${p.id}`} key={p.id} className="related-card" onClick={() => window.scrollTo(0, 0)}>
                                    <img src={p.urls.small} alt={p.alt_description || 'Related photo'} />
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default PhotoDetail;
